<template>
  <div class="slider-question__container">
    <div class="slider-question__content">
      <HintText>
        {{ c('slider.drag_the_slider') }}
      </HintText>

      <div v-if="showValues" class="slider-question__bounds">
        <span data-test-id="SliderQuestion_MinValue">{{ min }}</span>
        <span data-test-id="SliderQuestion_MaxValue">{{ max }}</span>
      </div>

      <div
        ref="track"
        data-test-id="SliderQuestion_Track"
        class="slider-question__track"
        role="slider"
        tabindex="0"
        :aria-valuemin="min"
        :aria-valuemax="max"
        :aria-valuenow="displayValueRounded"
        :aria-label="`${startLabel} to ${endLabel}`"
        @keydown.left.prevent="stepBy(-1)"
        @keydown.right.prevent="stepBy(1)"
        @pointerdown="onPointerDown"
      >
        <div
          v-if="showValues && isTouched"
          data-test-id="SliderQuestion_ValueReadout"
          class="slider-question__value-pin"
          :style="{ left: `${percent}%` }"
          aria-hidden="true"
        >
          <span class="slider-question__value-pin-shape">
            <span class="slider-question__value-pin-text">{{ displayValueRounded }}</span>
          </span>
        </div>

        <div class="slider-question__rail" />
        <div
          data-test-id="SliderQuestion_Handle"
          class="slider-question__handle"
          :class="{ 'slider-question__handle--touched': isTouched }"
          :style="{ left: `${percent}%` }"
        />
      </div>

      <div class="slider-question__labels">
        <p data-test-id="SliderQuestion_StartLabel">{{ startLabel }}</p>
        <p data-test-id="SliderQuestion_EndLabel">{{ endLabel }}</p>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, type DeepReadonly, defineComponent, type PropType, ref } from 'vue'

import { type Answer } from '../../../answer/models/Answer'
import { c } from '../../../intl'
import HintText from '../../../question/components/dumb/HintText.vue'

export default defineComponent({
  components: { HintText },
  props: {
    answers: {
      type: Array as PropType<DeepReadonly<Answer[]>>,
      required: true,
    },
    startLabel: {
      type: String,
      required: true,
    },
    endLabel: {
      type: String,
      required: true,
    },
    min: {
      type: Number,
      required: true,
    },
    max: {
      type: Number,
      required: true,
    },
    step: {
      type: Number,
      required: true,
    },
    showValues: {
      type: Boolean,
      required: true,
    },
  },
  emits: {
    setAnswer: (_: { fieldId: string; value: Answer<'slider'>['value'] }) => true,
  },
  setup(props, ctx) {
    const track = ref<HTMLElement | null>(null)

    const sliderAnswer = computed(() => {
      const [answer] = props.answers.flatMap((a) => (a.type === 'slider' ? [a] : []))
      if (!answer) throw Error('Could not find slider answer')
      return answer
    })

    /** Where the handle sits before the respondent touches it. */
    const startPosition = computed(() => clampToStep((props.min + props.max) / 2))

    /**
     * The stored value stays undefined until the handle is moved, which is what gates Next. The
     * handle is still drawn at the centre from the start, so display position and answered-ness
     * are separate concepts — the respondent sees a handle but has not answered.
     */
    const isTouched = computed(() => sliderAnswer.value.value.value !== undefined)
    const displayValue = computed(() => sliderAnswer.value.value.value ?? startPosition.value)
    // Round only for what the respondent sees — the stored value stays exact.
    // clampToStep does `Math.round((raw - min) / step) * step + min`, which
    // drifts on non-integer steps (0.1 + 0.2 = 0.30000000000000004). The editor
    // caps step size at two decimal places, so two decimals here is safe.
    const displayValueRounded = computed(() => Math.round(displayValue.value * 100) / 100)
    const percent = computed(
      () => ((displayValue.value - props.min) / (props.max - props.min)) * 100,
    )

    function clampToStep(raw: number): number {
      const stepped = Math.round((raw - props.min) / props.step) * props.step + props.min
      return Math.min(props.max, Math.max(props.min, stepped))
    }

    const commit = (value: number): void => {
      ctx.emit('setAnswer', { fieldId: 'slider', value: { value } })
    }

    /** Arrow keys move by exactly one step, from the centre if untouched. */
    const stepBy = (direction: -1 | 1): void => {
      commit(clampToStep(displayValue.value + direction * props.step))
    }

    /** Tap or drag moves to the nearest step under the pointer. */
    const valueFromClientX = (clientX: number): number | undefined => {
      const el = track.value
      if (!el) return undefined
      const { left, width } = el.getBoundingClientRect()
      if (width === 0) return undefined
      const ratio = Math.min(1, Math.max(0, (clientX - left) / width))
      return clampToStep(props.min + ratio * (props.max - props.min))
    }

    const onPointerDown = (event: PointerEvent): void => {
      const el = track.value
      if (!el) return
      el.focus()

      const apply = (clientX: number): void => {
        const value = valueFromClientX(clientX)
        if (value !== undefined) commit(value)
      }

      apply(event.clientX)

      const onMove = (moveEvent: PointerEvent): void => apply(moveEvent.clientX)
      const onUp = (): void => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    }

    return {
      track,
      isTouched,
      displayValueRounded,
      percent,
      stepBy,
      onPointerDown,
      c,
    }
  },
})
</script>
<style lang="postcss">
.slider-question {
  &__container {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--attest-spacing-6);
    box-sizing: border-box;
  }

  &__content {
    width: 100%;
    max-width: 32rem;
    display: flex;
    flex-direction: column;
  }

  &__track {
    position: relative;
    height: 2.5rem;
    display: flex;
    align-items: center;
    cursor: pointer;
    touch-action: none;
  }

  &__rail {
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background-color: var(--color-blue-grey);
  }

  &__handle {
    position: absolute;
    top: 50%;
    width: 1.5rem;
    height: 1.5rem;
    margin-left: -0.75rem;
    border-radius: 50%;
    background-color: var(--color-selected);
    border: 2px solid var(--color-selected);
    transform: translateY(-50%);
    transition: box-shadow 0.15s ease-in-out;

    &--touched {
      box-shadow: 0 0 0 4px color-mix(in srgb, var(--color-selected) 25%, transparent);
    }
  }

  &__bounds {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    width: 100%;
    gap: var(--attest-spacing-4);
    color: var(--color-disabled-text);
  }

  &__labels {
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    width: 100%;
    gap: var(--attest-spacing-4);

    p {
      flex: 1;
      min-width: 0;
    }

    p:first-child {
      text-align: left;
    }

    p:last-child {
      text-align: right;
    }
  }

  &__value-pin {
    position: absolute;
    bottom: 100%;
    margin-bottom: 0.75rem;
    transform: translateX(-50%);
    pointer-events: none;
  }

  &__value-pin-shape {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50% 50% 50% 0;
    background-color: var(--color-selected);
    transform: rotate(-45deg);
  }

  &__value-pin-text {
    display: block;
    transform: rotate(45deg);
    color: white;
    font-size: 0.75rem;
    font-weight: bold;
  }
}
</style>
